// src/pages/CategoriesPage.tsx
import React, { useEffect, useMemo } from "react";
import { useAdminStore } from "@/store/useAdminStore";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/Components/ui/button";
import { MainProduct } from "@/types/productTypes";
import { formatMoney } from "@/utils/money-format.helper";
import { useTranslation } from "@/hooks/use-translation";

function useQuery() {
   return new URLSearchParams(useLocation().search);
}

const CategoriesPage: React.FC = function () {
   const query = useQuery();
   const navigate = useNavigate();
   const rawCategory = query.get("category");
   const categoryIdValue =
      rawCategory !== null ? parseInt(rawCategory, 10) : null;

   const adminStore = useAdminStore();
   const allProducts = adminStore.allProducts;
   const isLoadingProducts = adminStore.isLoadingProducts;
   const fetchProducts = adminStore.fetchProducts;

   const translationResult = useTranslation();
   const translation = translationResult.translation;
   const localizePath = translationResult.localizePath;

   // Load products on first mount
   useEffect(
      function () {
         if (allProducts.length === 0) {
            fetchProducts();
         }
      },
      [allProducts.length, fetchProducts]
   );

   // Reset scroll when category tab changes
   useEffect(
      function () {
         window.scrollTo(0, 0);
      },
      [categoryIdValue]
   );

   // Filter out hidden products and by category
   const filteredProducts = useMemo(
      function () {
         return allProducts.filter(function (product) {
            if (product.hide) {
               return false;
            }
            if (categoryIdValue === null) {
               return true;
            }
            return product.productCategoryId === categoryIdValue;
         });
      },
      [allProducts, categoryIdValue]
   );

   // Navigate to a given category tab
   function goToCategory(tabId: number | null) {
      let pathString: string;
      if (tabId === null) {
         pathString = "/categories";
      } else {
         pathString = "/categories?category=" + tabId;
      }
      const localized = localizePath(pathString);
      navigate(localized);
   }

   return (
      <div className="px-4 sm:px-10 pb-20 mt-20 sm:mt-32 bg-grey">
         <div className="flex flex-col items-center text-center">
            <h1 className="text-d-h1 text-custom-dark">
               {translation.categories.heading}
            </h1>
            <p className="text-d-body-l-paragraph mt-4 text-custom-dark">
               {translation.categories.description}
            </p>

            <div className="mt-10 w-full sm:w-auto overflow-x-auto">
               <div className="flex flex-row gap-1 bg-custom-grey p-1 rounded-full min-w-max">
                  <Button
                     variant="tab-white-selected"
                     className="w-auto sm:w-40"
                     onClick={function () {
                        goToCategory(null);
                     }}
                  >
                     {translation.categories.tabs.allModels}
                  </Button>
               </div>
            </div>
         </div>

         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 sm:mt-20 px-4 sm:px-0">
            {isLoadingProducts
               ? Array.from({ length: 6 }).map(function (_, index) {
                    return <ProductCardSkeleton key={index} />;
                 })
               : filteredProducts.map(function (product) {
                    return <ProductCard key={product.id} product={product} />;
                 })}
         </div>
      </div>
   );
};

type ProductCardProps = {
   product: MainProduct;
};

const ProductCard: React.FC<ProductCardProps> = function (props) {
   const navigate = useNavigate();
   const translationResult = useTranslation();
   const localizePath = translationResult.localizePath;
   const product = props.product;
   const { translation } = useTranslation();
   function handleConfigureClick() {
      const queryString = "?product=" + encodeURIComponent(product.name);
      const pathString = "/products" + queryString;
      const localized = localizePath(pathString);
      navigate(localized);
   }

   return (
      <div className="flex flex-col rounded-lg overflow-hidden shadow-sm">
         <img
            src={product.image?.url ?? ""}
            alt={product.name}
            className="w-full h-40 sm:h-48 object-cover"
         />

         <div className="flex flex-col p-6 bg-custom-grey">
            <h4 className="text-d-h4 text-custom-dark">{product.name}</h4>
            <div className="text-d-body-m-line mt-2 text-custom-dark/60">
               {" "}
               {translation.categories.starts}{" "}
                {formatMoney(product.initialPrice, { currency: "EUR" })}
             </div>
            <Button
               variant="m-white"
               className="w-full mt-5"
               onClick={handleConfigureClick}
            >
               {translation.categories.button.configure}
            </Button>
         </div>
      </div>
   );
};

const ProductCardSkeleton: React.FC = function () {
   return (
      <div className="flex flex-col rounded-lg overflow-hidden shadow-sm animate-pulse">
         <div className="w-full h-40 sm:h-48 bg-gray-300" />
         <div className="flex flex-col p-6 bg-custom-grey">
            <div className="h-6 w-3/4 bg-gray-300 rounded mb-2" />
            <div className="h-4 w-1/2 bg-gray-300 rounded" />
            <div className="h-10 w-full bg-gray-200 mt-5 rounded" />
         </div>
      </div>
   );
};

export default CategoriesPage;
